// WHAT: Custom hook for managing the creation of new TimeTracQ workspaces
// WHY:  Centralizes logic for adding new workspaces via backend commands, ensuring consistent error/success handling
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: Tauri core API, React hooks
// RISKS:
//   - WARN: No validation of workspace name format or uniqueness before sending to backend.
//   - NOTE: Error handling may not provide specific failure reasons from the backend command.

import { core } from "@tauri-apps/api";
import { useCallback } from "react";

/**
 * WHAT:
 *   Custom hook to manage the creation of new workspaces in the TimeTracQ application.
 *
 * WHY:
 *   Encapsulates the asynchronous logic for interacting with the backend to add a new workspace,
 *   providing a clean and reusable interface for workspace creation.
 *
 * PARAMETERS:
 *   - setMessage: A state setter function for message state.
 *   - setSaving: A state setter function for the saving status.
 *   - onCreationSuccess: A callback function to execute after a workspace is successfully created.
 *
 * RETURNS:
 *   An object containing the `addWorkspace` function.
 *
 * @returns {object} Object with `addWorkspace` function
 *
 * RISKS:
 *   - WARN: The `addWorkspace` function performs basic trimming but no comprehensive validation
 *           of the workspace name (e.g., for special characters or uniqueness) before invoking the backend command.
 *   - NOTE: Error messages are generic; specific failure reasons from the backend might not be propagated to the user.
 *
 * @param setMessage
 *
 * @param setSaving
 *
 * @param onCreationSuccess
 *
 */
export function useWorkspaceCreation(
  setMessage: React.Dispatch<
    React.SetStateAction<null | { type: "error" | "success"; text: string }>
  >,
  setSaving: React.Dispatch<React.SetStateAction<boolean>>,
  onCreationSuccess: () => Promise<void>,
) {
  /**
   * WHAT:
   *   Creates a new workspace in the database and triggers a refresh of the configuration.
   *
   * WHY:
   *   Allows users to add new workspaces for organizing time tracking aspects.
   *
   * INPUTS:
   *   - newWorkspaceName: string - The name for the new workspace.
   *
   * SIDE EFFECTS:
   *   - Invokes a backend command (`add_workspace_cmd`) to create the workspace in the database.
   *   - Updates success/error messages in the UI.
   *   - Executes `onCreationSuccess` callback, which typically reloads the configuration and workspace names.
   *
   * RISKS:
   *   - WARN: No validation of workspace name format or uniqueness is performed before sending to backend.
   *   - NOTE: The workspace name is trimmed but not validated for special characters.
   *   - WARN: Error handling may not provide specific failure reasons from the backend command.
   */
  const addWorkspace = useCallback(
    async (newWorkspaceName: string) => {
      if (!newWorkspaceName.trim()) {
        setMessage({ text: "Workspace Name ist erforderlich", type: "error" });
        return;
      }

      try {
        setSaving(true);
        setMessage(null);

        await core.invoke("add_workspace_cmd", {
          name: newWorkspaceName.trim(),
        });
        setMessage({
          text: `Workspace "${newWorkspaceName}" erfolgreich erstellt!`,
          type: "success",
        });
        await onCreationSuccess();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Unbekannter Fehler beim Erstellen des Workspaces";
        setMessage({
          text: `Fehler beim Erstellen des Workspaces: ${message}`,
          type: "error",
        });
      } finally {
        setSaving(false);
      }
    },
    [setMessage, setSaving, onCreationSuccess],
  );

  return { addWorkspace };
}
